<template>
  <BaseModal
    :heading="$t('admin.treatwell.employee_availability_modal.heading')"
    :loading="loading"
    small
    :parentRoute="{ name: 'treatwell-admin' }"
    testId="task-employee-availability"
  >
    <Flex direction="column">
      <BaseText>
        {{ $t('admin.treatwell.employee_availability_modal.description') }}
      </BaseText>
      <BaseText
        size="s"
        color="secondary"
      >
        {{ $t('admin.treatwell.employee_availability_modal.days_hint') }}
      </BaseText>
      <BaseAlert
        v-if="saveFailed"
        color="error"
        :text="$t('admin.treatwell.employee_availability_modal.save_failed')"
        v-test="'employee-availability-error'"
      />
      <BaseText
        v-if="!loading && !employees.length"
        v-test="'employee-availability-empty'"
      >
        {{ $t('admin.treatwell.employee_availability_modal.empty') }}
      </BaseText>
      <EmployeeCard
        v-for="(employee, index) in employees"
        :key="employee.employeeId"
        v-model="employees[index]"
      />
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="close"
        v-test="'employee-availability-cancel'"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="isSaving"
        :disabled="!employees.length"
        @click="submit"
        v-test="'employee-availability-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { flash } from '@/helpers/ui';
import type {
  DaysOfWeek,
  TreatwellEmployeeAvailability,
  TreatwellMarketplaceHours,
} from '@/types';
import {
  GET_TREATWELL_EMPLOYEE_AVAILABILITIES,
  UPDATE_TREATWELL_EMPLOYEE_AVAILABILITIES,
} from '../../graphql';
import { WEEK } from './days';
import EmployeeCard from './EmployeeCard.vue';
import type { EmployeeForm } from './types';

const emit = defineEmits(['close', 'event']);

const router = useRouter();
const { t } = useI18n();

// A day that's not closed is bookable on the marketplace with the roster hours. Without
// marketplace days yet, everything is ticked so the salon only unticks the days they don't want.
const toDays = (
  weeklyHours: TreatwellMarketplaceHours[] | null | undefined,
): DaysOfWeek[] =>
  WEEK.filter(
    (day) => !weeklyHours?.some((hours) => hours.day === day && hours.closed),
  );

const employees = ref<EmployeeForm[]>([]);

const { onResult, loading } = useQuery(
  GET_TREATWELL_EMPLOYEE_AVAILABILITIES,
  null,
  { fetchPolicy: 'network-only' },
);

onResult(({ data }) => {
  employees.value = (data?.treatwellEmployeeAvailabilities || []).map(
    (employee: TreatwellEmployeeAvailability) => ({
      employeeId: employee.employeeId,
      name: employee.name,
      onMarketplace: employee.onMarketplace,
      customDays: !!employee.weeklyHours?.length,
      days: toDays(employee.weeklyHours),
    }),
  );
});

const close = () => {
  router.push({ name: 'treatwell-admin' });
};

const { mutate, loading: isSaving } = useMutation(
  UPDATE_TREATWELL_EMPLOYEE_AVAILABILITIES,
);

// The flash message is always a green success message, so a failed save shows in the modal instead
const saveFailed = ref(false);

const submit = () => {
  saveFailed.value = false;
  mutate({
    input: {
      employees: employees.value.map((employee) => ({
        employeeId: employee.employeeId,
        onMarketplace: employee.onMarketplace,
        // Only the unticked days are sent, as closed. The other days keep following the roster.
        weeklyHours: employee.customDays
          ? WEEK.filter((day) => !employee.days.includes(day)).map((day) => ({
              day,
              closed: true,
            }))
          : null,
      })),
    },
  }).then((response) => {
    if (response?.data?.treatwellUpdateEmployeeAvailabilities?.errors?.length) {
      saveFailed.value = true;
      return;
    }

    flash(t('admin.treatwell.employee_availability_modal.saved'));
    emit('event', 'set_employee_availability');
    close();
  });
};
</script>
